import { classnames, getRangeInputError } from '@aibees/shared/utils';
import { Box, InputAdornment, OutlinedInput } from '@mui/material';
import React, { memo, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../icon';
import { FilterInputError } from './components/filter-input-error';
import { ErrorMessage } from './config';
import { type NumberRangeInputProps as Props } from './types';

/**
 * NumberRangeInput Component
 *
 * PERFORMANCE OPTIMIZATION NOTE:
 * This component uses refs and direct DOM manipulation instead of useState to avoid
 * React re-renders during rapid input changes. This approach was chosen because:
 *
 * 1. Previous useState implementation caused race conditions between external value
 *    updates and user input, leading to stale state and poor UX
 * 2. Rapid external value changes (from parent components) were not immediately
 *    reflected in the UI due to React's reconciliation delays
 * 3. Every keystroke was triggering re-renders, causing performance issues
 *
 * The ref-based approach provides:
 * - Immediate visual feedback without React reconciliation delays
 * - Better handling of rapid external value changes
 * - No race conditions between internal and external state
 * - Improved performance for high-frequency updates
 */
export const NumberRangeInput = memo(
  ({
    className,
    icon,
    max,
    min,
    value,
    onChangeRange,
    error,
    isPercent,
    acceptNegative,
  }: Props) => {
    // Use refs for direct DOM manipulation to avoid React re-renders
    // This provides immediate visual feedback and better performance
    const input1Ref = useRef<HTMLInputElement>(null);
    const input2Ref = useRef<HTMLInputElement>(null);

    // Internal state stored in ref to avoid triggering re-renders
    // This prevents race conditions between external value updates and user input
    const stateRef = useRef({ v1: value?.at(0) ?? '', v2: value?.at(1) ?? '' });

    /**
     * Handle changes to the start (minimum) input
     * Updates both internal state and DOM directly for immediate feedback
     */
    const handleChangeStart = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        // Update internal state immediately
        stateRef.current.v1 = newValue;

        // Direct DOM update for instant visual feedback
        // This bypasses React's reconciliation for better performance
        if (input1Ref.current) {
          input1Ref.current.value = newValue;
        }

        // Notify parent component of change
        onChangeRange?.(newValue, stateRef.current.v2);
      },
      [onChangeRange]
    );

    /**
     * Handle changes to the end (maximum) input
     * Updates both internal state and DOM directly for immediate feedback
     */
    const handleChangeEnd = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        // Update internal state immediately
        stateRef.current.v2 = newValue;

        // Direct DOM update for instant visual feedback
        // This bypasses React's reconciliation for better performance
        if (input2Ref.current) {
          input2Ref.current.value = newValue;
        }

        // Notify parent component of change
        onChangeRange?.(stateRef.current.v1, newValue);
      },
      [onChangeRange]
    );

    /**
     * Sync internal state with external value changes
     * This ensures the component stays in sync with parent component updates
     * without causing unnecessary re-renders
     */
    useMemo(() => {
      // Only update if external value differs from internal state
      if (value?.at(0) !== stateRef.current.v1) {
        stateRef.current.v1 = value?.at(0) ?? '';
        // Update DOM to reflect external changes immediately
        if (input1Ref.current) {
          input1Ref.current.value = stateRef.current.v1;
        }
      }
      if (value?.at(1) !== stateRef.current.v2) {
        stateRef.current.v2 = value?.at(1) ?? '';
        // Update DOM to reflect external changes immediately
        if (input2Ref.current) {
          input2Ref.current.value = stateRef.current.v2;
        }
      }
    }, [value]);

    /**
     * Calculate input validation errors
     * Direct function call since ref values don't trigger re-renders
     * and getRangeInputError is fast enough to not need memoization
     */
    const inputError = getRangeInputError(
      stateRef.current.v1,
      stateRef.current.v2,
      acceptNegative
    );

    const placeholder = (text: string) => {
      return isPercent ? `${text} '%` : text;
    };

    const inputProps = {
      min: min ?? 0,
      max,
    };
    const InputProps = {
      startAdornment: icon && (
        <Icon name={icon} size={16} color="grey.400" sxx={{ minWidth: 16 }} />
      ),
      endAdornment: (!!inputError || error) && (
        <InputAdornment position="end">
          <Icon name="info" color="error" size={16} />
        </InputAdornment>
      ),
    };

    return (
      <Box
        className={classnames('NumberRangeInput', className)}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <OutlinedInput
            type="number"
            autoComplete="off"
            placeholder={placeholder('Min')}
            size="small"
            inputProps={inputProps}
            startAdornment={InputProps.startAdornment}
            endAdornment={InputProps.endAdornment}
            inputRef={input1Ref}
            defaultValue={stateRef.current.v1}
            onChange={handleChangeStart}
            error={error || !!inputError}
          />
          -
          <OutlinedInput
            type="number"
            autoComplete="off"
            placeholder={placeholder('Max')}
            size="small"
            inputProps={inputProps}
            startAdornment={InputProps.startAdornment}
            endAdornment={InputProps.endAdornment}
            inputRef={input2Ref}
            defaultValue={stateRef.current.v2}
            onChange={handleChangeEnd}
            error={error || !!inputError}
          />
        </Box>
        {!!inputError && (
          <FilterInputError message={ErrorMessage[inputError]} />
        )}
      </Box>
    );
  }
);

NumberRangeInput.displayName = 'NumberRangeInput';
